import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApartment } from '@/api/generated/sdk.gen';
import type { CreateApartmentRequest } from '@/api/generated/types.gen';
import { ApiError } from '@/lib/api';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Button, buttonVariants } from '@/components/ui/button';

export const Route = createFileRoute('/_app/apartments/new')({
  component: NewApartmentPage,
});

// Same nullable-flattening problem as the edit form: @hey-api/openapi-ts
// drops the `| null` arm. The POST contract accepts JSON null for the
// nullable optionals; cast through here so empty inputs send null on the
// wire.
function rawBody<T extends CreateApartmentRequest>(b: Record<string, unknown>): T {
  return b as unknown as T;
}

interface FormState {
  title: string;
  code: string;
  currency: string;
  timezone: string;
  occupancy_default: string;
  occupancy_max: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  latitude: string;
  longitude: string;
  email: string;
  phone: string;
  nickname: string;
  description_long: string;
  bedrooms: string;
  bathrooms: string;
  area_sqm: string;
  floor: string;
  house_rules: string;
  check_in_from: string;
  check_out_until: string;
  amenities: string;
}

const initialForm: FormState = {
  title: '',
  code: '',
  currency: 'EUR',
  timezone: 'Europe/Belgrade',
  occupancy_default: '2',
  occupancy_max: '4',
  address: '',
  city: '',
  state: '',
  country: '',
  zip_code: '',
  latitude: '',
  longitude: '',
  email: '',
  phone: '',
  nickname: '',
  description_long: '',
  bedrooms: '',
  bathrooms: '',
  area_sqm: '',
  floor: '',
  house_rules: '',
  check_in_from: '',
  check_out_until: '',
  amenities: '',
};

function trimOrUndef(v: string): string | undefined {
  const t = v.trim();
  return t === '' ? undefined : t;
}

function intOrNull(v: string): number | null | undefined {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) ? n : undefined;
}

function floatOrNull(v: string): number | null | undefined {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
}

function NewApartmentPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState<string | null>(null);

  // Title lives in the Topbar (navTitle → "Apartments"); publish the create
  // context as the subtitle. The breadcrumb below keeps the back affordance.
  usePageHeader({ subtitle: 'New apartment' });

  const mut = useMutation({
    mutationFn: async () => {
      // Required-field guards client-side (server re-validates with
      // ErrValidation → 400 on its end).
      const occupancyDefault = Number.parseInt(form.occupancy_default, 10);
      const occupancyMax = Number.parseInt(form.occupancy_max, 10);
      if (!form.title.trim()) throw new Error('Title is required.');
      if (!form.code.trim()) throw new Error('Code is required.');
      if (!/^[A-Z]{3}$/.test(form.currency)) throw new Error('Currency must be a 3-letter ISO-4217 code.');
      if (!form.timezone.trim()) throw new Error('Timezone is required (IANA, e.g. Europe/Berlin).');
      if (!Number.isInteger(occupancyDefault) || occupancyDefault < 1)
        throw new Error('Guests included in the price must be a positive integer.');
      if (!Number.isInteger(occupancyMax) || occupancyMax < 1)
        throw new Error('Max adults must be a positive integer.');
      if (occupancyDefault > occupancyMax)
        throw new Error('Guests included in the price must be ≤ max adults.');

      const bedrooms = intOrNull(form.bedrooms);
      const bathrooms = intOrNull(form.bathrooms);
      const floor = intOrNull(form.floor);
      const area = floatOrNull(form.area_sqm);
      const lat = floatOrNull(form.latitude);
      const lon = floatOrNull(form.longitude);
      if (bedrooms === undefined) throw new Error('Bedrooms must be a whole number.');
      if (bathrooms === undefined) throw new Error('Bathrooms must be a whole number.');
      if (floor === undefined) throw new Error('Floor must be a whole number.');
      if (area === undefined) throw new Error('Area must be a number.');
      if (lat === undefined) throw new Error('Latitude must be a number.');
      if (lon === undefined) throw new Error('Longitude must be a number.');

      const body = rawBody({
        title: form.title.trim(),
        code: form.code.trim(),
        currency: form.currency.trim(),
        timezone: form.timezone.trim(),
        occupancy_default: occupancyDefault,
        occupancy_max: occupancyMax,
        address: trimOrUndef(form.address),
        city: trimOrUndef(form.city),
        state: trimOrUndef(form.state),
        country: trimOrUndef(form.country),
        zip_code: trimOrUndef(form.zip_code),
        latitude: lat,
        longitude: lon,
        email: trimOrUndef(form.email),
        phone: trimOrUndef(form.phone),
        nickname: trimOrUndef(form.nickname) ?? null,
        description_long: trimOrUndef(form.description_long) ?? null,
        bedrooms,
        bathrooms,
        area_sqm: area,
        floor,
        house_rules: trimOrUndef(form.house_rules) ?? null,
        check_in_from: trimOrUndef(form.check_in_from) ?? null,
        check_out_until: trimOrUndef(form.check_out_until) ?? null,
        amenities: form.amenities
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      return createApartment({ throwOnError: true, body });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['apartments'] });
      toast({
        kind: 'success',
        title: 'Apartment created',
        message: `${form.title.trim()} is being provisioned with the channel manager.`,
      });
      const id = res.data?.id;
      if (id) {
        void navigate({ to: '/apartments/$id', params: { id } });
      } else {
        void navigate({ to: '/apartments' });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setError('Apartment creation is unavailable: the server is missing channel-manager credentials.');
          return;
        }
        if (err.status === 409) {
          setError('An apartment with this code already exists in this property.');
          return;
        }
        setError(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const set = <K extends keyof FormState>(k: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
  };

  return (
    <div className="max-w-3xl">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted">
        <Link to="/apartments" className="hover:underline">Apartments</Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">New</span>
      </nav>

      <p className="mb-6 text-sm text-muted">
        Creates an apartment locally and provisions a matching property,
        room type, and rate plan with the channel manager. If one of those calls
        fails mid-flow the reconciler will finish the row on its next tick.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mut.mutate();
        }}
        className="space-y-8"
      >
        <Section title="Basics" hint="Required for create.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Title *" id="ap-title">
              <input id="ap-title" type="text" required value={form.title} onChange={set('title')} className={inputCls} />
            </FormField>
            <FormField label="Code *" id="ap-code" hint="Short internal handle, unique per property.">
              <input
                id="ap-code"
                type="text"
                required
                value={form.code}
                onChange={set('code')}
                className={inputCls + ' font-mono'}
              />
            </FormField>
            <FormField label="Currency * (3-letter)" id="ap-currency">
              <input
                id="ap-currency"
                type="text"
                required
                value={form.currency}
                onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
                maxLength={3}
                className={inputCls + ' font-mono uppercase'}
              />
            </FormField>
            <FormField label="Timezone * (IANA)" id="ap-tz">
              <input id="ap-tz" type="text" required value={form.timezone} onChange={set('timezone')} className={inputCls} />
            </FormField>
            <FormField label="Guests included in the price *" id="ap-occ-def">
              <input
                id="ap-occ-def"
                type="number"
                required
                min={1}
                value={form.occupancy_default}
                onChange={set('occupancy_default')}
                className={inputCls}
              />
            </FormField>
            <FormField label="Max adults *" id="ap-occ-max">
              <input
                id="ap-occ-max"
                type="number"
                required
                min={1}
                value={form.occupancy_max}
                onChange={set('occupancy_max')}
                className={inputCls}
              />
            </FormField>
          </div>
        </Section>

        <Section
          title="Address & contact"
          hint="Optional, but OTAs (Booking.com, Airbnb) reject channel connection without these."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Address" id="ap-addr">
              <input id="ap-addr" type="text" value={form.address} onChange={set('address')} className={inputCls} />
            </FormField>
            <FormField label="City" id="ap-city">
              <input id="ap-city" type="text" value={form.city} onChange={set('city')} className={inputCls} />
            </FormField>
            <FormField label="State / region" id="ap-state">
              <input id="ap-state" type="text" value={form.state} onChange={set('state')} className={inputCls} />
            </FormField>
            <FormField label="Country (ISO 3166-1 alpha-2)" id="ap-country">
              <input
                id="ap-country"
                type="text"
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value.toUpperCase() }))}
                maxLength={2}
                className={inputCls + ' font-mono uppercase'}
              />
            </FormField>
            <FormField label="ZIP / postal code" id="ap-zip">
              <input id="ap-zip" type="text" value={form.zip_code} onChange={set('zip_code')} className={inputCls} />
            </FormField>
            <FormField label="Latitude" id="ap-lat">
              <input id="ap-lat" type="number" step="0.0000001" value={form.latitude} onChange={set('latitude')} className={inputCls} />
            </FormField>
            <FormField label="Longitude" id="ap-lon">
              <input id="ap-lon" type="number" step="0.0000001" value={form.longitude} onChange={set('longitude')} className={inputCls} />
            </FormField>
            <FormField label="Email" id="ap-email">
              <input id="ap-email" type="email" value={form.email} onChange={set('email')} className={inputCls} />
            </FormField>
            <FormField label="Phone" id="ap-phone">
              <input id="ap-phone" type="tel" value={form.phone} onChange={set('phone')} className={inputCls} />
            </FormField>
          </div>
        </Section>

        <Section title="Metadata" hint="All optional, can be edited later.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Nickname" id="ap-nick" hint="Internal handle shown in operator lists.">
              <input id="ap-nick" type="text" value={form.nickname} onChange={set('nickname')} className={inputCls} />
            </FormField>
            <FormField label="Bedrooms" id="ap-bed">
              <input id="ap-bed" type="number" min={0} value={form.bedrooms} onChange={set('bedrooms')} className={inputCls} />
            </FormField>
            <FormField label="Bathrooms" id="ap-bath">
              <input id="ap-bath" type="number" min={0} value={form.bathrooms} onChange={set('bathrooms')} className={inputCls} />
            </FormField>
            <FormField label="Floor" id="ap-floor">
              <input id="ap-floor" type="number" value={form.floor} onChange={set('floor')} className={inputCls} />
            </FormField>
            <FormField label="Area (m²)" id="ap-area">
              <input id="ap-area" type="number" step="0.01" min={0} value={form.area_sqm} onChange={set('area_sqm')} className={inputCls} />
            </FormField>
            <FormField label="Check-in from" id="ap-checkin">
              <input id="ap-checkin" type="time" value={form.check_in_from} onChange={set('check_in_from')} className={inputCls} />
            </FormField>
            <FormField label="Check-out until" id="ap-checkout">
              <input id="ap-checkout" type="time" value={form.check_out_until} onChange={set('check_out_until')} className={inputCls} />
            </FormField>
          </div>
          <FormField label="Description" id="ap-desc" className="mt-4">
            <textarea id="ap-desc" rows={4} value={form.description_long} onChange={set('description_long')} className={inputCls} />
          </FormField>
          <FormField label="House rules" id="ap-rules" className="mt-4">
            <textarea id="ap-rules" rows={3} value={form.house_rules} onChange={set('house_rules')} className={inputCls} />
          </FormField>
          <FormField
            label="Amenities (comma-separated codes: wifi, kitchen, washer, …)"
            id="ap-amenities"
            className="mt-4"
          >
            <input id="ap-amenities" type="text" value={form.amenities} onChange={set('amenities')} className={inputCls + ' font-mono'} />
          </FormField>
        </Section>

        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create apartment'}
          </Button>
          <Link to="/apartments" className={buttonVariants({ variant: 'secondary' })}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]';

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">{title}</h2>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function FormField({
  label,
  id,
  hint,
  className = '',
  children,
}: {
  label: string;
  id: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}
